import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { OperatorForm } from "./operator.schema";
import { Input } from "@/src/components/ui/input";
import { Textarea } from "@/src/components/ui/textarea";
import { ScoreSelect } from "@/src/components/ui/score-select";
import { Label } from "@radix-ui/react-label";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { YachtLocation } from "../model";
import { extractFromMultiName } from "../../Locations/utils";

export type GeneralInfoProps = {
  locations: YachtLocation[];
};

export function GeneralInfo({ locations }: GeneralInfoProps) {
  const groupedLocations = useMemo(
    () =>
      locations.reduce(
        (acc, location) => {
          const country = extractFromMultiName(location.town?.country.names, "Unknown country");
          const existing = acc.find((item) => item.country === country);
          const locationName = extractFromMultiName(location.names);
          if (existing) {
            existing.names.push(locationName);
            existing.names.sort();
          } else {
            acc.push({ country, names: [locationName] });
          }
          return acc.sort((a, b) => a.country.localeCompare(b.country));
        },
        [] as { country: string; names: string[] }[],
      ),
    [locations],
  );

  return (
    <div className="grid gap-4 max-w-[1016px] grid-cols-[repeat(auto-fit,minmax(150px,328px))]">
      <FormField<OperatorForm, "operator.name">
        name="operator.name"
        render={({ field }) => (
          <FormItem required>
            <FormLabel>Operator name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.contactPerson">
        name="operator.contactPerson"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Contact person</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.email">
        name="operator.email"
        render={({ field }) => (
          <FormItem required>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.email2">
        name="operator.email2"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email (post booking)</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.phone">
        name="operator.phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Phone</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.address">
        name="operator.address"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address</FormLabel>
            <FormControl>
              <Textarea className="resize-none" {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.rating">
        name="operator.rating"
        render={({ field }) => (
          <FormItem required>
            <FormLabel>Rating</FormLabel>
            <FormControl>
              <ScoreSelect min={1} max={10} {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.ratingHelm">
        name="operator.ratingHelm"
        render={({ field }) => (
          <FormItem required>
            <FormLabel>Helm rating</FormLabel>
            <FormControl>
              <ScoreSelect min={1} max={10} {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.ratingAnchor">
        name="operator.ratingAnchor"
        render={({ field }) => (
          <FormItem required>
            <FormLabel>Anchor rating</FormLabel>
            <FormControl>
              <ScoreSelect min={1} max={10} {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<OperatorForm, "operator.website">
        name="operator.website"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Website</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      {!!groupedLocations.length && (
        <div className="flex flex-col gap-2">
          <Label>Locations</Label>
          <ScrollArea className="max-h-36 overflow-x-hidden max-w-full rounded-md border">
            <ul className="max-w-full list-disc list-inside pl-3">
              {groupedLocations.map((location) => (
                <Fragment key={location.country}>
                  <li className="font-bold">{location.country}</li>
                  <ul className="list-disc list-inside pl-3">
                    {location.names.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </Fragment>
              ))}
            </ul>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
